import type { FloorDisplayVM } from '@Components/BigBrowse/Views/FloorCellView/FloorCellLayoutViewModel';

import type { CardTextProps } from '../GridCardMesh/Components/Texts/CardText';
import type {
  BrandConcessionContentDM,
  HeaderBackgroundView,
  HeroAndTextView,
  JumpCardView,
  MerchandiserView,
  MultiSectionFigmaLayout,
  ProductSetContentDM,
  SectionBackgroundView,
  SectionItemDM,
  ThreeUpView,
} from '../Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import type { GridAbstract } from './Classes/GridAbstract';
import type { GridCard } from './Classes/GridCard';

import type { JourneyParameters } from '~types/navigation';

export type PlaneBox = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

export type PlaneSize = {
  width: number;
  height: number;
};

export type Position2D = {
  x: number;
  y: number;
};

export type GridCardView = {
  id: string;
  key: string;
  imageUrl?: string;
  title?: string;
  body?: string;
  product?: ProductSetContentDM['products'][0];
  parentHero?: GridCardView;
  destination?: JourneyParameters;
  isViewHero?: boolean;
  sectionId?: number;
  type: undefined; // GridCardView should not have a layout type like Merchandiser, ThreeUp, etc.
};

export type GridCardViewMultiSectionHero = GridCardView & {
  dx: number; // a displacement to allow the hero positions not-aligned with columns
  dataModel: BrandConcessionContentDM;
  fontColor?: string;
};

export type GridCardViewMultiSectionDescription =
  GridCardViewMultiSectionHero & {
    layout: MultiSectionFigmaLayout;
  };

export type GridCardViewSectionHero = GridCardView & {
  dataModel: BrandConcessionContentDM;
  layout: MultiSectionFigmaLayout;
  sectionModel: SectionItemDM;
};

export enum PlaneTypes {
  Card_BigBrowse_Bookend = 'Card_BigBrowse_Bookend',
  Card_BigBrowse_Product = 'Card_BigBrowse_Product',
  Card_ShopFloor_Hero = 'Card_ShopFloor_Hero',
  Card_ShopFloor_Product = 'Card_ShopFloor_Product',
  Grid_SingleSectionView = 'Grid_SingleSectionView',
  Card_SingleSectionView_Hero = 'Card_SingleSectionView_Hero',
  Card_SingleSectionView_Product = 'Card_SingleSectionView_Product',
  Card_SingleSectionView_Description = 'Card_SingleSectionView_Description',
  Card_SingleSectionView_Description_Body = 'Card_SingleSectionView_Description_Body',
  Card_SingleSectionView_Subtitle = 'Card_SingleSectionView_Subtitle',
  Card_MultiSection_Hero = 'Card_MultiSection_Hero',
  Card_MultiSection_SectionHero = 'Card_MultiSection_SectionHero',
  Card_MultiSection_SectionHero_Small = 'Card_MultiSection_SectionHero_Small',
  Card_MultiSection_SectionHero_Small_Title = 'Card_MultiSection_SectionHero_Small_Title',
  Card_MultiSection_SectionHero_Small_Description = 'Card_MultiSection_SectionHero_Small_Description',
  Card_MultiSection_Footer_Item = 'Card_MultiSection_Footer_Item',
  Card_MultiSection_Footer_Item_Small = 'Card_MultiSection_Footer_Item_Small',
  Card_MultiSection_Description = 'Card_MultiSection_Description',
  Card_MultiSection_Description_Small = 'Card_MultiSection_Description_Small',
  Card_MultiSection_Description_Body_Small = 'Card_MultiSection_Description_Body_Small',
  Card_MultiSection_Description_Body_Ultra_Small = 'Card_MultiSection_Description_Body_Ultra_Small',
  Card_MultiSection_Description_Title = 'Card_MultiSection_Description_Title',
  Card_MultiSection_Description_Title_Small = 'Card_MultiSection_Description_Title_Small',
  Card_MultiSection_Description_Title_Ultra_Small = 'Card_MultiSection_Description_Title_Ultra_Small',
  Card_MultiSection_Product = 'Card_MultiSection_Product',
  Card_MultiSection_Subtitle = 'Card_MultiSection_Subtitle',
  Card_MultiSection_Subtitle_Ultra_Small = 'Card_MultiSection_Subtitle_Ultra_Small',
  Card_Background_Plane = 'Card_BackgroundPlane',
  Layout_Merchandiser = 'Layout_Merchandiser',
  Layout_ThreeUp = 'Layout_ThreeUp',
  Layout_HeroAndText_ImagePart = 'Layout_HeroAndText_ImagePart',
  Layout_HeroAndText_TextPart = 'Layout_HeroAndText_TextPart',
  Section_Background = 'Section_Background',
  Section_Background_Marker = 'Section_Background_Marker',
  Header_Background = 'Header_Background',
  Header_Background_Marker = 'Header_Background_Marker',
  Gap = 'Gap',
}

type GridCardDataMap = {
  [PlaneTypes.Card_BigBrowse_Bookend]: GridCardView;
  [PlaneTypes.Card_BigBrowse_Product]: GridCardView;
  [PlaneTypes.Card_ShopFloor_Hero]: GridCardView;
  [PlaneTypes.Card_ShopFloor_Product]: GridCardView;
  [PlaneTypes.Grid_SingleSectionView]: GridCardView;
  [PlaneTypes.Card_SingleSectionView_Hero]: GridCardView;
  [PlaneTypes.Card_SingleSectionView_Product]: GridCardView;
  [PlaneTypes.Card_SingleSectionView_Description]: GridCardView;
  [PlaneTypes.Card_SingleSectionView_Description_Body]: GridCardView;
  [PlaneTypes.Card_SingleSectionView_Subtitle]: GridCardView;
  [PlaneTypes.Card_MultiSection_Hero]: GridCardViewMultiSectionHero;
  [PlaneTypes.Card_MultiSection_SectionHero]: GridCardViewSectionHero;
  [PlaneTypes.Card_MultiSection_SectionHero_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_SectionHero_Small_Title]: GridCardView;
  [PlaneTypes.Card_MultiSection_SectionHero_Small_Description]: GridCardView;
  [PlaneTypes.Card_MultiSection_Footer_Item]: JumpCardView & {
    layout: MultiSectionFigmaLayout;
  };
  [PlaneTypes.Card_MultiSection_Footer_Item_Small]: JumpCardView;
  [PlaneTypes.Card_MultiSection_Description]: GridCardViewMultiSectionDescription;
  [PlaneTypes.Card_MultiSection_Description_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_Description_Body_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_Description_Body_Ultra_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_Description_Title]: GridCardView;
  [PlaneTypes.Card_MultiSection_Description_Title_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_Description_Title_Ultra_Small]: GridCardView;
  [PlaneTypes.Card_MultiSection_Product]: GridCardView;
  [PlaneTypes.Card_MultiSection_Subtitle]: GridCardView & {
    layout: MultiSectionFigmaLayout;
    style?: Partial<CardTextProps>;
  };
  [PlaneTypes.Card_MultiSection_Subtitle_Ultra_Small]: GridCardView & {
    style?: CardTextProps;
  };
  [PlaneTypes.Card_Background_Plane]: GridCardView;
  [PlaneTypes.Layout_Merchandiser]: MerchandiserView;
  [PlaneTypes.Layout_ThreeUp]: ThreeUpView;
  [PlaneTypes.Layout_HeroAndText_ImagePart]: HeroAndTextView;
  [PlaneTypes.Layout_HeroAndText_TextPart]: HeroAndTextView;
  [PlaneTypes.Section_Background]: SectionBackgroundView;
  [PlaneTypes.Section_Background_Marker]: SectionBackgroundView;
  [PlaneTypes.Header_Background]: HeaderBackgroundView;
  [PlaneTypes.Header_Background_Marker]: HeaderBackgroundView;
  [PlaneTypes.Gap]: undefined;
};

export type GridCardData<T extends PlaneTypes> = GridCardDataMap[T];

export type IPlaneParent = {
  worldPosition: Position2D;
};

export type IScrollTarget = Record<string, { x: number; y: number }>;

export type GridLayout<D = undefined> = {
  size: PlaneSize;
  items: GridCard<PlaneTypes>[];
  subGrids?: GridAbstract<any>[];
  scrollBox?: PlaneBox;
  modelData?: D;
  scrollTargets?: IScrollTarget;
  displays?: { model: FloorDisplayVM; box: PlaneBox; position: Position2D }[];
};

export type CameraProps = {
  size: PlaneSize;
  position: Position2D;
  box: PlaneBox;
  zoom: number;
};

type AnimatedTransform<T> = {
  to: T;
  from?: T;
  onAnimEnd?: () => void;
};

export type ItemTransform = {
  opacity?: AnimatedTransform<number>;
  position?: AnimatedTransform<Position2D>;
  size?: AnimatedTransform<PlaneSize>;
  scale?: AnimatedTransform<number>;
};

export type ColumnConfig = {
  numOfColumns: number;
  reminderPx: number;
};

export type PlaneBoundaries = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};
